// frontend/src/pages/RefundPolicy.jsx
import { useEffect } from "react";
import HeaderSimple from "../components/pages/home/HeaderSimple";
import Footer from "../components/pages/home/Footer";
import { updatePageTitle } from "../utils/titleUtils";

const RefundPolicy = () => {
    useEffect(() => {
        updatePageTitle("Refund Policy");
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-bg-60">
            <HeaderSimple />

            <main className="pt-48 pb-20 px-6">
                <div className="w-[90%] max-w-5xl mx-auto">
                    <div className="text-center mb-10">
                        <h1 className="text-4xl font-bold text-fg-50 mb-2">Refund Policy</h1>
                    </div>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">1. Overview</h2>
                        <p className="text-fg-60">
                            At LeebAI, we strive to provide a reliable and high-quality AI platform. If you experience any issues or believe you are entitled to a refund, please review the terms below.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">2. Subscription Payments</h2>
                        <ul className="list-disc pl-5 text-fg-60 space-y-2">
                            <li>All subscription fees (monthly or annual) are charged upfront at the beginning of each billing cycle.</li>
                            <li>Subscription payments are non-refundable, except in the specific cases outlined below.</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">3. Eligible Refund Cases</h2>

                        <div className="mb-4">
                            <h3 className="text-xl font-medium text-fg-50 mb-2">3.1 Accidental or Duplicate Charges</h3>
                            <p className="text-fg-60 mb-2">We will issue a full refund if:</p>
                            <ul className="list-disc pl-5 text-fg-60 space-y-2">
                                <li>You were charged twice for the same billing period.</li>
                                <li>You were mistakenly charged after cancelling your subscription on time.</li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h3 className="text-xl font-medium text-fg-50 mb-2">3.2 Technical Issues Preventing Use</h3>
                            <p className="text-fg-60 mb-2">Refunds may be granted if:</p>
                            <ul className="list-disc pl-5 text-fg-60 space-y-2">
                                <li>A major platform malfunction prevented you from accessing LeebAI.</li>
                                <li>Our system was unavailable for an extended period (48+ hours) and the issue originated on our side.</li>
                                <li>You must contact support within 7 days of the issue occurring.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-xl font-medium text-fg-50 mb-2">3.3 Unauthorized Charges (Account Compromise)</h3>
                            <p className="text-fg-60">
                                If your account was compromised and unauthorized payments were made, we will fully refund the affected transactions after verifying the case.
                            </p>
                        </div>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">4. Non-Refundable Cases</h2>

                        <div className="mb-4">
                            <h3 className="text-xl font-medium text-fg-50 mb-2">4.1 Change of Mind</h3>
                            <p className="text-fg-60 mb-2">We do not offer refunds for:</p>
                            <ul className="list-disc pl-5 text-fg-60 space-y-2">
                                <li>"I didn't use the service."</li>
                                <li>"I changed my mind."</li>
                                <li>"I meant to cancel earlier."</li>
                            </ul>
                        </div>

                        <div className="mb-4">
                            <h3 className="text-xl font-medium text-fg-50 mb-2">4.2 Usage-Based Charges</h3>
                            <p className="text-fg-60 mb-2">Because usage credits consume real compute costs, extra usage charges are non-refundable, unless:</p>
                            <ul className="list-disc pl-5 text-fg-60 space-y-2">
                                <li>There was a clear system bug.</li>
                                <li>You were charged beyond the maximum usage cap (if applicable).</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-xl font-medium text-fg-50 mb-2">4.3 Promotional or Discounted Plans</h3>
                            <p className="text-fg-60">
                                Payments made through special offers, reduced pricing, or trial upgrades are non-refundable.
                            </p>
                        </div>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">5. Cancellation Policy</h2>
                        <ul className="list-disc pl-5 text-fg-60 space-y-2">
                            <li>You can cancel anytime from your dashboard.</li>
                            <li>Cancellation stops future renewal charges.</li>
                            <li>Access continues until the end of the paid period.</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">6. How to Request a Refund</h2>
                        <p className="text-fg-60 mb-2">To request a refund, contact us at <a href="mailto:support@leeb.ai" className="text-primary-50 hover:underline">support@leeb.ai</a> with:</p>
                        <ul className="list-disc pl-5 text-fg-60 space-y-2 mb-2">
                            <li>Your account email</li>
                            <li>Transaction ID</li>
                            <li>Reason for your request</li>
                            <li>Any relevant screenshots or error details</li>
                        </ul>
                        <p className="text-fg-60">We aim to respond within 48 hours.</p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">7. Decisions & Processing Time</h2>
                        <ul className="list-disc pl-5 text-fg-60 space-y-2">
                            <li>Approved refunds will be processed back to your original payment method within 3–10 business days depending on your bank or card provider.</li>
                            <li>Refund decisions are final.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-fg-50 mb-3">8. Abuse Prevention</h2>
                        <p className="text-fg-60 mb-2">LeebAI reserves the right to deny refunds for:</p>
                        <ul className="list-disc pl-5 text-fg-60 space-y-2">
                            <li>Repeated refund requests</li>
                            <li>Suspicious or abusive usage patterns</li>
                            <li>Users violating our Terms of Service</li>
                        </ul>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default RefundPolicy;
